'use client';
import { JSX, ReactElement } from 'react';
import { useToaster, toast as _toast, resolveValue } from 'react-hot-toast/headless';
import cn from 'classnames';
import { FlexWrapper } from '../FlexWrapper';
import { FontIcon, FontIconColor, FontIconName } from '../FontIcon';
import { useMediaQuery } from '@intigral/hooks/dom/useMediaQuery/useMediaQuery';
import styles from './styles.module.sass';

// https://react-hot-toast.com/docs/use-toaster
export const Notifications = ({ duration = 5000 }: { duration?: number }) => {
    const { toasts, handlers } = useToaster({ duration });
    const { startPause, endPause, calculateOffset, updateHeight } = handlers;
    // keep in sync with points.$medium in styles.module.sass
    const isDesktop = useMediaQuery('(min-width: 924px)');

    interface IParams extends CustomToastData {
        toastId: string;
    }
    type RenderDismissButtonFn = (params: IParams) => ReactElement;
    const renderDismissButton: RenderDismissButtonFn = ({ buttonText, onButtonClick, toastId }) => {
        return buttonText ? (
            <button onClick={onButtonClick} className={styles.button}>
                {buttonText}
            </button>
        ) : (
            <span className={styles.dismissBtn} onClick={() => _toast.dismiss(toastId)}>
                <FontIcon name="close" size="smaller" md="smaller" />
            </span>
        );
    };

    return (
        <div className={styles.toaster} onMouseEnter={startPause} onMouseLeave={endPause}>
            {toasts.map((toast) => {
                // Extract custom data from the toast
                const { buttonText, onButtonClick, notDismissible } = toast as unknown as CustomToastData;

                const slidingDirection: 1 | -1 = isDesktop ? -1 : 1;
                const offset = calculateOffset(toast, {
                    reverseOrder: false,
                    gutter: 6 * slidingDirection,
                });

                const ref = (el: HTMLDivElement) => {
                    if (el && typeof toast.height !== 'number') {
                        const height = el.getBoundingClientRect().height * slidingDirection;
                        updateHeight(toast.id, height);
                    }
                };

                return (
                    <div
                        data-test-id="toast-message"
                        key={toast.id}
                        ref={ref}
                        style={{
                            opacity: toast.visible ? 1 : 0,
                            transform: `translateY(${offset}px)`,
                        }}
                        className={cn(styles.toast, toast.className || styles.dark)}
                        {...toast.ariaProps}
                    >
                        <FlexWrapper flexGrow>
                            {toast.icon && <div className={styles.icon}>{toast.icon}</div>}
                            <FlexWrapper flexGrow>{resolveValue(toast.message, toast)}</FlexWrapper>
                        </FlexWrapper>
                        {!notDismissible && renderDismissButton({ buttonText, onButtonClick, toastId: toast.id })}
                    </div>
                );
            })}
        </div>
    );
};

/** Toast message is either a string or function that returns JSX markup */
type Message = string | (() => JSX.Element);
export type ToastType = 'success' | 'error' | 'custom' | 'default' | 'dark';

const toHeadlessType = (type: ToastType) => (type === 'default' || type === 'dark' ? 'success' : type);

type CustomToastData = {
    icon?: ReactElement;
    buttonText?: string;
    onButtonClick?: () => void;
    className?: string;
    /** if `true` 'close' ('dismiss') button is not rendered */
    notDismissible?: boolean;
    /** @deprecated */
    shouldSlideDown?: boolean;
};

export const toast = (message: Message, type: ToastType = 'success', customData?: CustomToastData) => {
    let iconName: FontIconName = 'check-circle';

    const content = typeof message === 'string' ? <span dangerouslySetInnerHTML={{ __html: message }} /> : message;

    if (type === 'error') {
        iconName = 'alert';
    }

    _toast[toHeadlessType(type)](content, {
        icon:
            customData?.icon ??
            (type !== 'custom' ? (
                <FontIcon name={iconName} size="smaller" md="smaller" color={type === 'error' ? 'red' : 'green'} />
            ) : undefined),
        ...customData,
        // duration: 600000,
    });
};
